// Splice an accepted "Ask Scribe" rewrite into a scene element's DOM and return
// the new scene HTML. Non-destructive to the manuscript until the caller saves:
// this only edits the in-page rendered copy to produce the new content string,
// which is then persisted through the safe save path (snapshot + save + reindex).

import type { ReviewAnchor } from "../lib/types.js";
import { buildReadModel, rangeFromOffsets, resolveAnchor } from "./anchor.js";

/** Build a fragment of the replacement prose (newlines → <br>). */
function fragmentFor(text: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  const lines = text.split(/\n/);
  lines.forEach((line, i) => {
    if (i > 0) frag.appendChild(document.createElement("br"));
    frag.appendChild(document.createTextNode(line));
  });
  return frag;
}

/**
 * Replace the anchored selection inside `sceneEl` with `newText` and return the
 * element's resulting innerHTML, or null if the anchor can't be resolved (so the
 * caller can refuse rather than apply to the wrong place).
 */
export function spliceRewrite(
  sceneEl: HTMLElement,
  anchor: ReviewAnchor | null,
  selectedText: string | null,
  newText: string
): string | null {
  const model = buildReadModel(sceneEl);
  // Prefer the exact stored offsets; fall back to content relocation.
  let range: Range | null = null;
  if (anchor?.start !== undefined && anchor.end !== undefined && model.text.slice(anchor.start, anchor.end) === selectedText) {
    range = rangeFromOffsets(model, anchor.start, anchor.end);
  } else {
    range = resolveAnchor(model, anchor, selectedText).range;
  }
  if (!range) return null;
  range.deleteContents();
  range.insertNode(fragmentFor(newText));
  sceneEl.normalize();
  return sceneEl.innerHTML;
}
